import React, { useState } from 'react';
import { Check, Star, Crown, ChevronDown } from 'lucide-react';
import { packages } from '../data';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Breadcrumb } from '../components/Breadcrumb';
import { PackageDetailModal } from '../components/PackageDetailModal';
import SEO from '../components/SEO';

const Packages = ({ onQuotationClick }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  return (
    <div>
      <SEO
        title="Packages"
        description="Explore Aashi Constructions' construction packages in Bangalore. Find the perfect plan for your home or commercial project."
        keywords="construction packages bangalore, building packages, home construction plans"
        url="/packages"
      />
      <Breadcrumb items={[{ label: 'Packages' }]} />
      
      {/* Hero Section */}
      <section className="relative h-64 sm:h-80 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.pexels.com/photos/35116768/pexels-photo-35116768.jpeg?auto=compress&cs=tinysrgb&w=1200&q=40"
          alt="Packages"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-3 sm:mb-4">
            Elegance Packages
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            Choose from our curated construction packages designed to match your budget and requirements. Each package is customizable to your unique needs.
          </p>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative rounded-xl md:rounded-2xl shadow-xl transition-all duration-500 hover:scale-105 ${
                  pkg.isSignature
                    ? 'bg-gradient-to-br from-brand-50 to-white/40 border-2 border-brand-600 md:scale-105 xl:scale-110 xl:z-10'
                    : 'bg-white border border-gray-200'
                }`}
              >
                {pkg.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-brand-600 text-white border-0 px-4 sm:px-6 py-1 text-xs sm:text-sm font-semibold shadow-lg">
                      {pkg.badge}
                    </Badge>
                  </div>
                )}

                <div className="p-6 sm:p-8">
                  {/* Package Icon */}
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-4 sm:mb-6 mx-auto ${
                    pkg.isSignature ? 'bg-brand-100' : 'bg-gray-100'
                  }`}>
                    {pkg.isSignature ? (
                      <Crown className="w-7 h-7 sm:w-8 sm:h-8 text-brand-600" />
                    ) : pkg.id === 1 ? (
                      <Star className="w-7 h-7 sm:w-8 sm:h-8 text-gray-600" />
                    ) : (
                      <Star className="w-7 h-7 sm:w-8 sm:h-8 text-gray-600" />
                    )}
                  </div>

                  {/* Package Name */}
                  <h3 className={`text-xl sm:text-2xl font-bold text-center mb-2 ${
                    pkg.isSignature ? 'text-brand-900' : 'text-gray-900'
                  }`}>
                    {pkg.name}
                  </h3>

                  {/* Description */}
                  <p className="text-center text-gray-600 text-sm sm:text-base mb-4 sm:mb-6 min-h-[40px] sm:min-h-[48px]">
                    {pkg.description}
                  </p>

                  {/* Price */}
                  <div className={`text-center mb-6 sm:mb-8 pb-6 sm:pb-8 border-b ${
                    pkg.isSignature ? 'border-brand-200' : 'border-gray-200'
                  }`}>
                    <p className={`text-2xl sm:text-3xl font-bold ${
                      pkg.isSignature ? 'text-brand-600' : 'text-gray-900'
                    }`}>
                      {pkg.price}
                    </p>
                  </div>

                  {/* Features */}
                  {/* <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className={`w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 flex-shrink-0 mt-0.5 ${
                          pkg.isSignature ? 'text-brand-600' : 'text-gray-600'
                        }`} />
                        <span className="text-gray-700 text-xs sm:text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul> */}

                  {/* CTA Buttons */}
                  <div className="space-y-2">
                    <Button
                      onClick={onQuotationClick}
                      className={`w-full transition-all duration-300 h-10 sm:h-11 text-sm sm:text-base ${
                        pkg.isSignature
                          ? 'bg-brand-600 hover:bg-brand-700 text-white shadow-lg hover:shadow-xl'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      }`}
                    >
                      Request Quotation
                    </Button>
                    {/* <button
                      onClick={() => setSelectedPackage(pkg)}
                      className="w-full text-center text-xs sm:text-sm text-brand-600 hover:text-brand-700 font-medium py-2 hover:bg-brand-50 rounded-lg transition-colors duration-200"
                    >
                      View Full Details →
                    </button> */}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-12 sm:mt-16 text-center px-4">
            <p className="text-gray-600 text-base sm:text-lg mb-4">
              All packages are fully customizable to your specific needs and preferences
            </p>
            <Button
              onClick={onQuotationClick}
              variant="outline"
              className="border-brand-600 text-brand-600 hover:bg-brand-50 h-10 sm:h-11 px-6 sm:px-8 text-sm sm:text-base"
            >
              Schedule a Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Custom Package CTA */}
      <section className="py-12 md:py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-brand-600 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-600 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6">
            Need a Custom Package?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-10">
            Every project is unique. Let's create a custom package tailored specifically to your needs and budget.
          </p>
          <Button
            onClick={onQuotationClick}
            size="lg"
            className="bg-brand-600 hover:bg-brand-700 text-white px-8 sm:px-12 py-4 sm:py-6 text-base sm:text-lg font-medium shadow-2xl hover:shadow-brand-600/50 transition-all duration-300 hover:scale-105"
          >
            Design Your Custom Package
          </Button>
        </div>
      </section>

      {/* Package Detail Modal */}
      <PackageDetailModal
        pkg={selectedPackage}
        onClose={() => setSelectedPackage(null)}
      />
    </div>
  );
};

export default Packages;
